'use strict'

// OOTB Config
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'

// Custom Utils
import {getLocalStorageJSONItem} from '../utils/utils'
import {findLowestPrice} from '../utils/product-utils'
import {getDefaultShipment} from '../utils/basket-utils'
import {isIncludedTax, showOtherTaxAndDuties, isZonosTaxesUsed} from '../utils/price-utils'
import currencyList from '../util/currencyList'

// Constants
import {GTM_GC_PAYMENT_ID} from '../util/constants'

/**
 * Builds analytics data for a page view event, capturing various user and environment details.
 *
 * @param {string} pageType - The type of the page being viewed (e.g., 'home', 'product', 'checkout').
 * @param {Object} customer - The customer object containing user details. Expected to have properties like `creationDate`, `customerNo`, and custom attributes prefixed with `c_`.
 * @param {Object} basket - The basket object containing details about the current shopping basket. Expected to have properties like `c_paymentCurrencyCode`, `currency`, and custom attributes prefixed with `c_`.
 * @param {Object} shipToCountry - The country object representing the shipping destination. Expected to have an `id` property.
 * @param {Object} locale - The locale object representing the current language and region settings. Expected to have an `id` property.
 * @param {Object} site - The site object representing the current website. Expected to have an `id` property.
 * @param {string} customerType - The type of customer ('registered' or other types).
 * @param {string} activeCurrency - The active currency code if not specified in the basket.
 *
 * @returns {Object} An object containing analytics data for the page view, including:
 * - `cookie_ga_optout`: Boolean indicating if Google Analytics cookies are opted out.
 * - `cookie_ad_optout`: Boolean indicating if advertising cookies are opted out.
 * - `cookie_sn_optout`: Boolean indicating if social network cookies are opted out.
 * - `environment_shipto`: The ID of the shipping destination country.
 * - `currency`: The currency code used for the transaction.
 * - `environment_language`: The ID of the current language and region settings.
 * - `environment_website`: The determined environment website based on the site ID.
 * - `page_type`: The type of the page being viewed.
 * - `uv`: An object containing user variables, such as:
 *   - `id`: Customer number if the customer is registered.
 *   - `us`: Custom user attribute if the customer is registered.
 *   - `sd`: Account creation year if the customer is registered.
 *   - Custom attributes (`he`, `hp`, `hf`, `hl`, `hz`, `hc`, `hco`, `hs`, `hb`) from either the customer or basket, depending on the customer type.
 */
export const buildAnalyticsPageViewData = (
    pageType,
    customer,
    basket,
    shipToCountry,
    locale,
    site,
    customerType,
    activeCurrency
) => {
    try {
        let cookieAccepted = getLocalStorageJSONItem('cookiesAccepted')
        let cookie_ga_optout = getLocalStorageJSONItem('cookie_ga')
        let cookie_ad_optout = getLocalStorageJSONItem('cookie_ad')
        let cookie_sn_optout = getLocalStorageJSONItem('cookie_sn')

        cookie_ga_optout =
            typeof cookie_ga_optout === 'undefined'
                ? cookieAccepted !== 'true'
                : cookie_ga_optout !== 'true'
        cookie_ad_optout =
            typeof cookie_ad_optout === 'undefined'
                ? cookieAccepted !== 'true'
                : cookie_ad_optout !== 'true'
        cookie_sn_optout =
            typeof cookie_sn_optout === 'undefined'
                ? cookieAccepted !== 'true'
                : cookie_sn_optout !== 'true'

        // user type
        const isRegisteredCustomer = customerType == 'registered'

        // Account Creation Date
        let year = ''
        if (customerType == 'registered') {
            const dateString = customer?.creationDate
            const date = new Date(dateString)
            year = date.getUTCFullYear()
        }

        return {
            cookie_ga_optout: cookie_ga_optout,
            cookie_ad_optout: cookie_ad_optout,
            cookie_sn_optout: cookie_sn_optout,
            environment_shipto: shipToCountry?.id,
            currency: basket?.c_paymentCurrencyCode || basket?.currency || activeCurrency,
            environment_language: locale?.id,
            environment_website: determineEnvironmentWebsite(site?.id),
            page_type: pageType,
            uv: {
                id: isRegisteredCustomer ? customer?.customerNo : '',
                us: isRegisteredCustomer ? customer?.c_us : null,
                sd: isRegisteredCustomer ? year : '',
                he: isRegisteredCustomer ? customer?.c_he : basket?.c_he ? basket?.c_he : '',
                hp: isRegisteredCustomer ? customer?.c_hp : basket?.c_hp ? basket?.c_hp : '',
                hf: isRegisteredCustomer ? customer?.c_hf : basket?.c_hf ? basket?.c_hf : '',
                hl: isRegisteredCustomer ? customer?.c_hl : basket?.c_hl ? basket?.c_hl : '',
                hz: isRegisteredCustomer ? customer?.c_hz : basket?.c_hz ? basket?.c_hz : '',
                hc: isRegisteredCustomer ? customer?.c_hc : basket?.c_hc ? basket?.c_hc : '',
                hco: isRegisteredCustomer ? customer?.c_hco : basket?.c_hco ? basket?.c_hco : '',
                hs: isRegisteredCustomer ? customer?.c_hs : basket?.c_hs ? basket?.c_hs : '',
                hb: isRegisteredCustomer ? customer?.c_hb : basket?.c_hb ? basket?.c_hb : '',
                or: isRegisteredCustomer ? customer?.c_or : basket?.c_or ? basket?.c_or : '',
                lt: isRegisteredCustomer ? customer?.c_lt : basket?.c_lt ? basket?.c_lt : '',
                ot: isRegisteredCustomer ? customer?.c_ot : basket?.c_ot ? basket?.c_ot : '',
                oc: isRegisteredCustomer ? customer?.c_oc : basket?.c_oc ? basket?.c_oc : ''
            }
        }
    } catch (error) {
        // Return a default or empty object in case of error
        return {
            cookie_ga_optout: null,
            cookie_ad_optout: null,
            cookie_sn_optout: null,
            environment_shipto: null,
            currency: activeCurrency,
            environment_language: null,
            environment_website: null,
            page_type: pageType,
            uv: {
                id: '',
                us: null,
                sd: '',
                he: '',
                hp: '',
                hf: '',
                hl: '',
                hz: '',
                hc: '',
                hco: '',
                hs: '',
                hb: '',
                or: '',
                lt: '',
                ot: '',
                oc: ''
            }
        }
    }
}

/**
 * Determines the environment and site type for a given site and returns a formatted string.
 *
 * This function evaluates the current environment based on the organization ID from the configuration
 * and determines whether it is a production or staging environment. It also identifies the site type
 * based on the site ID and returns a string combining both the environment and site type.
 *
 * @param {Object} site - The site object containing the site ID.
 * @param {string} site.id - The identifier of the site, used to determine the site type.
 * @returns {string} A string representing the combined environment and site type, formatted as
 *                   'environmentType-siteType'. Possible values include 'production-fullprice',
 *                   'production-outlet', 'staging-fullprice', 'staging-outlet', or 'staging-unknown'.
 */
const determineEnvironmentWebsite = (site) => {
    const config = getConfig()

    // Determine if the environment is production
    const isProduction = config.app.commerceAPI.parameters.organizationId === 'f_ecom_blgh_prd'

    // Determine the environment type
    const environmentType = isProduction ? 'production' : 'staging'

    // Determine the site type
    const siteType = (() => {
        switch (site) {
            case 'VBQ-EU':
            case 'VBQ-US':
                return 'fullprice'
            case 'VBQ-OUTLET-EU':
            case 'VBQ-OUTLET-US':
                return 'outlet'
            default:
                return 'unknown' // Handle unexpected site IDs
        }
    })()

    // Combine the environment and site type
    return `${environmentType}-${siteType}`
}

/**
 * Constructs an analytics data object for newsletter subscription events.
 *
 * @param {string} pagePosition - The position on the page where the newsletter subscription event occurred.
 * @returns {Object} An object containing the analytics data for the newsletter subscription, including the position.
 */
export const buildAnalyticsNewsletterSubscriptionData = (pagePosition) => {
    return {
        position: pagePosition
    }
}

/*
 * Constructs an analytics data object for related product clicks.
 *
 * This function generates an object containing analytics data for tracking
 * clicks on related products. It extracts the product name and URL from
 * the provided product object and formats them into a standardized structure.
 *
 * @param {Object} product - The product object containing details of the related product.
 * @param {string} product.name - The name of the product.
 * @param {string} product.productUrl - The URL of the product.
 * @returns {Object} An object containing the analytics data for the related product click.
 * @returns {string} return.link_text - The text of the link, derived from the product name.
 * @returns {string} return.link_url - The URL of the link, derived from the product URL.
 */
export const buildAnalyticsClickRelatedProductsData = (product) => {
    return {
        link_text: product?.name,
        link_url: product?.productUrl
    }
}

/*
 * Constructs an analytics event data object for a search event.
 *
 * @param {string} searchTerm - The search term entered by the user.
 * @returns {Object} An object containing the search term used in the analytics event.
 */
export const buildAnalyticsSearchEventData = (searchTerm) => {
    return {
        search_term: searchTerm
    }
}

/*
 * Constructs and returns an object representing analytics event data for a sign-up action.
 * This function is used to build the data payload for tracking user sign-up events in analytics.
 *
 * @returns {Object} An object containing the analytics event data for a sign-up action.
 *                   The object includes the following properties:
 *                   - method: {string} The method used for sign-up, set to 'account'.
 */
export const buildAnalyticsSignUpEventData = () => {
    return {
        method: 'account'
    }
}

/**
 * Constructs an analytics event data object for a login event.
 * This function is used to build a standardized object that can be sent
 * to analytics services to track login events within the application.
 *
 * @returns {Object} An object containing the login method used,
 *                   currently set to 'form'.
 */
export const buildAnalyticsLoginEventData = () => {
    return {
        method: 'form'
    }
}

/**
 * Builds a structured analytics data object for checkout events, suitable for use with analytics platforms (e.g., Google Analytics 4).
 *
 * This function processes the current line item cart, product details, conversion rates, event type, and customer type
 * to generate a comprehensive analytics payload. The output includes item details, pricing, discounts, coupon codes,
 * shipping tiers, payment types, taxes, and transaction identifiers, depending on the event context.
 *
 * @param {Object} lineItemCrt - The current cart or order object, containing product items, shipments, pricing, adjustments, and custom attributes.
 * @param {Array<Object>} products - Array of product objects, each containing product metadata and category information.
 * @param {Object} conversionRate - Object containing currency conversion information, such as paymentCurrencyCode and lcpMultiplier.
 * @param {string} event - The analytics event type (e.g., 'add_shipping_info', 'add_payment_info', 'purchase').
 * @param {string} customerType - The type of customer ('registered' or 'guest').
 * @returns {Object} An analytics data object containing currency, value, coupon, items, cart type, shipping tier, payment type, tax, shipping, and transaction ID as applicable.
 */
export const buildAnalyticsCheckoutData = (
    lineItemCrt,
    products,
    conversionRate,
    event,
    customerType
) => {
    try {
        const currentCurrencyObj = currencyList.find(
            (currency) => currency.Code === lineItemCrt.currency
        )
        let decimalTruncator = Math.pow(10, currentCurrencyObj?.Decimals || '0')
        const items = lineItemCrt.productItems.map((item) => {
            const product = products.find((p) => p.id === item.productId)
            const categoriesData = getCategoriesFromStructuredData(product)
            const discountValue = conversionRate?.lcpMultiplier
                ? conversionRate?.lcpMultiplier * (item.price - item.priceAfterOrderDiscount)
                : item.price - item.priceAfterOrderDiscount
            return {
                item_name: item.productName,
                item_id: product?.c_orliweb_product_short_code,
                item_ean: product?.ean || '',
                ...categoriesData,
                price: conversionRate?.lcpMultiplier
                    ? item.price * conversionRate?.lcpMultiplier
                    : item.price,
                quantity: item.quantity,
                discount: Math.trunc(discountValue * decimalTruncator) / decimalTruncator,
                currency: lineItemCrt.currency,
                item_color: product?.variationValues?.color || '',
                item_size: product?.variationValues?.size || '',
                item_print: product?.c_orliweb_stat20_mat_rgt || '',
                item_provenance: item?.c_origin || ''
            }
        })

        // check type of object
        const isRegisteredCustomer = customerType == 'registered'

        // Extract coupon from lineItemCrt
        const coupon =
            lineItemCrt.priceAdjustments && lineItemCrt.priceAdjustments.length > 0
                ? lineItemCrt.priceAdjustments[0].lineItemText
                : ''

        const finalObject = {
            currency: conversionRate?.paymentCurrencyCode || lineItemCrt?.currency,
            value:
                lineItemCrt?.c_internationalShipping &&
                lineItemCrt?.c_internationalShippingOrderQuote
                    ? lineItemCrt?.c_internationalShippingOrderQuote
                    : lineItemCrt?.c_basketTotalWithGiftCertificatesApplied,
            coupon: coupon,
            items: items,
            cart_type: isRegisteredCustomer ? 'classic' : 'guest'
        }

        /*
         * shippingTier Section
         * Only for add_shipping_info and add_payment_info events
         */
        if (event == 'add_shipping_info' || event == 'add_payment_info' || event === 'purchase') {
            const shippingTiers = []
            // Use a mapping object to simplify conditional logic
            const shippingTierMap = {
                express: 'express',
                dayair: 'express',
                international: 'internationalshipping',
                shipfromstore: 'SFS',
                clickandcollecttoday: 'CCT',
                athometoday: 'AHT'
            }

            lineItemCrt?.shipments?.forEach((shipment) => {
                let shippingMethodId = shipment?.shippingMethod?.id?.toLowerCase()

                if (shippingMethodId) {
                    // Find the matching shipping tier or default to 'standard'
                    const tier =
                        Object.keys(shippingTierMap).find((key) =>
                            shippingMethodId.includes(key)
                        ) || 'standard'
                    shippingTiers.push(shippingTierMap[tier] || tier)
                }
            })

            const uniqueShippingTiers = [...new Set(shippingTiers)]

            finalObject.shipping_tier =
                uniqueShippingTiers.length > 0 ? uniqueShippingTiers.join('|') : 'standard'
        }

        /*
         * payment_type Section
         * Only add_payment_info event
         */
        if (event == 'add_payment_info' || event === 'purchase') {
            // Use c_Adyen_paymentMethod for mapping
            if (lineItemCrt?.c_Adyen_paymentMethod) {
                finalObject.payment_type = lineItemCrt?.c_Adyen_paymentMethod
            } else {
                const adyenOperations =
                    lineItemCrt?.c_adyen_operations && lineItemCrt?.c_adyen_operations.length > 0
                        ? JSON.parse(lineItemCrt?.c_adyen_operations[0])
                        : null

                const paymentTypes = []
                // Payment with Adyen
                if (adyenOperations?.response) {
                    if (adyenOperations.response?.paymentMethod?.brand) {
                        // payment methods without external redirection
                        paymentTypes.push(adyenOperations.response?.paymentMethod?.brand)
                    } else if (adyenOperations.response?.action?.paymentMethodType) {
                        // payment methods with external redirection
                        paymentTypes.push(adyenOperations.response?.action?.paymentMethodType)
                    }
                }
                // Payment with GC
                if (lineItemCrt?.paymentInstruments?.length > 0) {
                    paymentTypes.push(GTM_GC_PAYMENT_ID)
                }

                finalObject.payment_type = paymentTypes.length > 0 ? paymentTypes.join('|') : null
            }
        }

        if (event === 'purchase') {
            finalObject.shipping = lineItemCrt?.shippingTotal || 0
            const defaultShipment = getDefaultShipment(lineItemCrt)
            const countryCode = defaultShipment?.shippingAddress?.countryCode
            if (lineItemCrt?.c_internationalShipping) {
                if (!isIncludedTax(countryCode) && isZonosTaxesUsed(countryCode)) {
                    finalObject.tax = lineItemCrt?.c_internationalShippingZonosTax || 0
                } else if (showOtherTaxAndDuties(countryCode)) {
                    finalObject.tax = lineItemCrt?.c_internationalShippingZonosDuties || 0
                }
            } else {
                finalObject.tax = lineItemCrt?.taxTotal || 0
            }
            finalObject.transaction_id = lineItemCrt?.orderNo
            finalObject.value =
                lineItemCrt?.c_internationalShipping &&
                lineItemCrt?.c_internationalShippingOrderQuote
                    ? lineItemCrt?.c_internationalShippingOrderQuote
                    : lineItemCrt?.c_basketTotalWithGiftCertificatesApplied
        }

        return finalObject
    } catch (error) {
        // Return a default or empty object in case of error
        return {
            currency: null,
            value: null,
            coupon: '',
            items: [],
            shipping_tier: null
        }
    }
}

/**
 * Builds and returns an analytics-ready cart view data object for use in tracking and reporting.
 *
 * This function processes the basket and product data, applies currency conversion if needed,
 * calculates discounts, and structures the data according to analytics requirements. It also
 * handles errors gracefully by returning a default object if any exception occurs.
 *
 * @param {Object} categoriesTree - The hierarchical tree of product categories.
 * @param {Object} basket - The current basket object containing product items and pricing details.
 * @param {Object} products - A mapping of product IDs to product detail objects.
 * @param {Object} [conversionRate] - Optional conversion rate object with currency and multiplier info.
 * @param {string} [conversionRate.lcpMultiplier] - Multiplier for local currency price conversion.
 * @param {string} [conversionRate.paymentCurrencyCode] - Target currency code for analytics.
 * @returns {Object} An object containing:
 *   - {string|null} currency: The currency code used for analytics.
 *   - {number|null} value: The total value of the basket (with international shipping quote if applicable).
 *   - {Array<Object>} items: Array of item objects with analytics properties (name, id, ean, color, size, print, categories, price, quantity, discount, currency).
 */
export const buildAnalyticsCartViewData = (categoriesTree, basket, products, conversionRate) => {
    try {
        const currentCurrencyObj = currencyList.find(
            (currency) => currency.Code === basket.currency
        )
        let decimalTruncator = Math.pow(10, currentCurrencyObj?.Decimals || '0')
        // build object for analytics
        const items = basket.productItems.map((item) => {
            const product = products[item.productId]
            const {
                c_orliweb_product_short_code: item_id,
                c_subCategoryID: subCategoryID,
                ean = '',
                variationValues: {color = '', size = ''} = {},
                c_orliweb_stat20_mat_rgt: item_print = ''
            } = product || {}
            const categories = getItemCategories(categoriesTree, subCategoryID)

            const discountValue = conversionRate?.lcpMultiplier
                ? conversionRate?.lcpMultiplier * (item.price - item.priceAfterOrderDiscount)
                : item.price - item.priceAfterOrderDiscount
            return {
                item_name: item.productName,
                item_id,
                item_ean: ean,
                item_color: color,
                item_size: size,
                item_print,
                ...categories,
                price: conversionRate?.lcpMultiplier
                    ? conversionRate?.lcpMultiplier * item.price
                    : item.price,
                quantity: item.quantity,
                discount: Math.trunc(discountValue * decimalTruncator) / decimalTruncator,
                currency: basket?.currency
            }
        })

        return {
            currency: conversionRate?.paymentCurrencyCode || basket?.currency,
            value:
                basket?.c_internationalShipping && basket?.c_internationalShippingOrderQuote
                    ? basket?.c_internationalShippingOrderQuote
                    : basket?.c_basketTotalWithGiftCertificatesApplied,
            items: items
        }
    } catch (error) {
        // Return a default or empty object in case of error
        return {
            currency: null,
            value: null,
            items: []
        }
    }
}

/**
 * Constructs an analytics data object for adding a product to a wishlist.
 *
 * This function generates a structured data object that can be used for analytics
 * tracking when a product is added to a wishlist. It includes detailed information
 * about the product, such as its name, ID, price, and various attributes like size,
 * color, and category. The function also calculates any applicable discount based
 * on the product's lowest price.
 *
 * @param {Object} product - The product object containing details about the product.
 * @param {string} product.name - The name of the product.
 * @param {string} product.id - The unique identifier for the product.
 * @param {string} [product.ean] - The European Article Number (EAN) of the product.
 * @param {Object} [product.variationValues] - The variation values of the product.
 * @param {string} [product.variationValues.size] - The size variation of the product.
 * @param {string} [product.variationValues.color] - The color variation of the product.
 * @param {string} [product.c_orliweb_motif] - The motif or print of the product.
 * @param {string} [product.c_orliweb_product_model] - The product model or family.
 * @param {Array} [product.c_seoLinks] - An array of SEO link objects for the product.
 * @param {string} [product.c_seoLinks.displayName] - The display name of the category.
 * @param {number} product.price - The price of the product.
 * @param {string} product.currency - The currency code of the product's price.
 * @param {number} conversionRate - The conversion rate object containing currency information.
 * @param {string} conversionRate.paymentCurrencyCode - The currency code for payment conversion.
 *
 * @returns {Object} An object containing analytics data for the product added to the wishlist.
 * @returns {string} return.currency - The currency code used for the transaction.
 * @returns {number} return.value - The value of the product, equivalent to its price.
 * @returns {Array<Object>} return.items - An array containing a single item object with product details.
 * @returns {string} return.items[].item_name - The name of the product.
 * @returns {string} return.items[].item_id - The unique identifier for the product.
 * @returns {string} return.items
 */
export const buildAnalyticsAddToWishlistData = (product, categoriesTree, conversionRate) => {
    try {
        let items = []
        if (product?.hitType === 'variation_group') {
            const productData =
                product.hitType === 'variation_group' ? product.representedProduct : product

            const productLowestPrice = findLowestPrice(product)
            const discount =
                productLowestPrice.promotion == null
                    ? 0
                    : Number(product?.price - productLowestPrice.minPrice)
            const finalDiscount = conversionRate?.lcpMultiplier
                ? conversionRate?.lcpMultiplier * discount
                : discount
            const finalPrice = conversionRate?.lcpMultiplier
                ? conversionRate?.lcpMultiplier * product?.price
                : product?.price

            const categories = getCategoriesFromStructuredData(product)

            items = [
                {
                    item_name: product?.productName,
                    item_id: productData?.c_orliweb_product_short_code,
                    item_ean: productData?.id,
                    item_color: productData?.c_color,
                    item_family: productData?.c_orliweb_isFamily,
                    item_size: productData?.variationValues?.size,
                    item_print: productData?.c_orliweb_stat20_mat_rgt || '',
                    ...categories,
                    price: finalPrice,
                    quantity: 1, // Always 1 for wishlist
                    discount: finalDiscount,
                    currency: product?.currency
                }
            ]

            return {
                currency: conversionRate?.paymentCurrencyCode || product?.currency,
                value: product?.price, // Value is the same as price
                items: items
            }
        } else {
            const {
                name: item_name,
                c_orliweb_product_short_code: item_id,
                c_orliweb_stat20_mat_rgt: print,
                ean = '',
                variationValues: {color = '', size = ''} = {},
                c_orliweb_isFamily: item_family = '',
                price,
                currency
            } = product

            const productLowestPrice = findLowestPrice(product)
            const discount =
                productLowestPrice.promotion == null
                    ? 0
                    : Number(price - productLowestPrice.minPrice)
            const finalDiscount = conversionRate?.lcpMultiplier
                ? conversionRate?.lcpMultiplier * discount
                : discount
            const finalPrice = conversionRate?.lcpMultiplier
                ? conversionRate?.lcpMultiplier * price
                : price

            const subCategoryID =
                product?.representedProduct?.c_subCategoryID || product?.c_subCategoryID || ''
            const categories = getItemCategories(categoriesTree, subCategoryID)

            items = [
                {
                    item_name,
                    item_id,
                    item_ean: ean,
                    item_color: color,
                    item_size: size,
                    item_print: print,
                    item_family,
                    ...categories,
                    price: finalPrice,
                    quantity: 1, // Always 1 for wishlist
                    discount: finalDiscount,
                    currency
                }
            ]
            return {
                currency: conversionRate?.paymentCurrencyCode || currency,
                value: price, // Value is the same as price
                items: items
            }
        }
    } catch (error) {
        // Return a default or empty object in case of error
        return {
            currency: null,
            value: null,
            items: []
        }
    }
}

/**
 * Constructs an analytics data object for the "remove from cart" event.
 *
 * This function generates a structured data object that represents the details
 * of a product being removed from the shopping cart, which can be used for
 * analytics tracking purposes. It includes information about the product, its
 * pricing, and categorization.
 *
 * @param {Object} product - The product object containing details about the product.
 * @param {string} product.name - The name of the product.
 * @param {string} product.id - The unique identifier for the product.
 * @param {string} [product.ean] - The European Article Number of the product.
 * @param {Object} [product.variationValues] - The variation values of the product.
 * @param {string} [product.variationValues.size] - The size variation of the product.
 * @param {string} [product.variationValues.color] - The color variation of the product.
 * @param {string} [product.orliweb_stat20_mat_rgt] - Additional product attribute for print.
 * @param {Array} [product.c_seoLinks] - Array of SEO link objects for product categorization.
 * @param {string} [product.c_seoLinks[].displayName] - Display name of the category.
 * @param {number} product.price - The price of the product.
 * @param {string} product.currency - The currency of the product price.
 *
 * @param {Object} productItem - The product item object containing pricing details.
 * @param {number} productItem.price - The original price of the product item.
 * @param {number} productItem.priceAfterOrderDiscount - The price after applying order discounts.
 * @param {string} [productItem.currency] - The currency of the product item.
 *
 * @param {Object} [conversionsRate] - The conversion rate object for currency conversion.
 * @param {string} [conversionsRate.paymentCurrencyCode] - The currency code used for payment.
 *
 * @returns {Object} An object containing the analytics data for the "remove from cart" event.
 * @returns {string} return.currency - The currency code for the transaction.
 * @returns {number} return.value - The value of the product, equivalent to its price.
 * @returns {Array<Object>} return.items - An array containing the details of the removed product.
 * @returns {string} return.items[].item_name -
 */
export const buildAnalyticsRemoveFromCartData = (product, productItem, conversionsRate) => {
    try {
        const {
            name: item_name,
            c_orliweb_product_short_code: item_id,
            c_orliweb_stat20_mat_rgt: print,
            ean = '',
            variationValues: {color = '', size = ''} = {},
            currency
        } = product

        const finalPrice = conversionsRate?.lcpMultiplier
            ? conversionsRate?.lcpMultiplier * productItem.price
            : productItem.price
        const finalDiscount = conversionsRate?.lcpMultiplier
            ? conversionsRate?.lcpMultiplier *
              (productItem.price - productItem.priceAfterOrderDiscount)
            : productItem.price - productItem.priceAfterOrderDiscount
        const categories = getCategoriesFromStructuredData(product)
        const items = [
            {
                item_name,
                item_id,
                item_ean: ean,
                item_color: color,
                item_size: size,
                item_print: print,
                ...categories,
                price: finalPrice,
                quantity: 1, // Always 1 for remove from cart
                discount: finalDiscount,
                currency
            }
        ]

        return {
            currency: conversionsRate?.paymentCurrencyCode || productItem?.currency,
            value: product.price, // Value is the same as price
            items: items
        }
    } catch (error) {
        // Return a default or empty object in case of error
        return {
            currency: null,
            value: null,
            items: []
        }
    }
}

/**
 * Builds the analytics data object for an "add to cart" event, formatting product and pricing information
 * for use in analytics tracking (e.g., Google Analytics 4). Handles price conversions, discounts, and
 * category extraction, and gracefully manages errors.
 *
 * @param {Array<Object>} productItems - Array of product item objects in the cart, each containing product and pricing details.
 * @param {Object} product - The product object being added to the cart, including identifiers and attributes.
 * @param {Object} conversionsRate - Object containing conversion rates and currency codes for price calculations.
 * @param {Object} basket - The current basket object, used to retrieve currency and other basket-level data.
 * @param {string} [provenance] - Optional provenance information to be included in the analytics data.
 * @returns {Object} An object containing the currency and an array of item data formatted for analytics.
 *                   If an error occurs, returns an object with null currency, null value, and an empty items array.
 */
export const buildAnalyticsAddtoCartData = (
    productItems,
    product,
    conversionsRate,
    basket,
    provenance
) => {
    try {
        const productItem = productItems.find((item) => item.productId === product.productId)

        const {
            name = productItem?.productName,
            c_orliweb_product_short_code: item_id,
            c_orliweb_stat20_mat_rgt: print,
            ean = '',
            variationValues: {color = '', size = ''} = {},
            currency
        } = product

        const finalPrice = conversionsRate?.lcpMultiplier
            ? conversionsRate?.lcpMultiplier * productItem?.basePrice
            : productItem?.basePrice

        const totalDiscount = conversionsRate?.lcpMultiplier
            ? conversionsRate?.lcpMultiplier *
              (productItem?.price - productItem?.priceAfterOrderDiscount)
            : productItem?.price - productItem?.priceAfterOrderDiscount

        const finalDiscount = productItem?.quantity > 0 ? totalDiscount / productItem?.quantity : 0
        const categories = getCategoriesFromStructuredData(product)
        const items = [
            {
                item_name: name,
                item_id,
                item_ean: ean,
                item_color: color,
                item_size: size,
                item_print: print,
                ...categories,
                price: finalPrice,
                quantity: 1, // Always 1 for remove from cart
                discount: finalDiscount,
                currency: currency || productItem?.currency || basket?.currency
            }
        ]

        if (provenance) {
            items[0].item_provenance = provenance
        }

        return {
            currency: conversionsRate?.paymentCurrencyCode || basket?.currency || currency,
            items: items
        }
    } catch (error) {
        // Return a default or empty object in case of error
        return {
            currency: null,
            value: null,
            items: []
        }
    }
}

/**
 * Builds the analytics data object for a view item list event, formatting product information
 * according to the specified format for use in analytics tracking (e.g., Google Analytics 4).
 *
 * @param {Object} categoriesTree - The hierarchical tree of product categories for mapping category data.
 * @param {Array<Object>} products - The array of product objects to be included in the analytics item list.
 * @param {Object} basket - The current basket or cart object, used for fallback currency information.
 * @param {Object} conversionsRate - Object containing conversion rates and multipliers for price/discount calculations.
 * @param {string} [format='default'] - The format type for the item list ('default', 'alternative', or 'homepage').
 * @returns {Object} An object containing:
 *   - {string|null} currency: The currency code used for the analytics event (from conversion rate, basket, or product).
 *   - {Array<Object>|null} items: The formatted array of item objects for analytics, or null if an error occurs.
 */
export const buildAnalyticsViewItemListData = (
    categoriesTree,
    products,
    basket,
    conversionsRate,
    format = 'default'
) => {
    try {
        let auxCurrency = null
        const items = products.map((product) => {
            if (format === 'alternative') {
                // Handle the alternative data format
                const productLowestPrice = findLowestPrice(product)
                const discount =
                    productLowestPrice.promotion == null
                        ? 0
                        : Number(product?.price - productLowestPrice.minPrice)
                const finalDiscount = conversionsRate?.lcpMultiplier
                    ? conversionsRate?.lcpMultiplier * discount
                    : discount
                const finalPrice = conversionsRate?.lcpMultiplier
                    ? conversionsRate?.lcpMultiplier * product?.price
                    : product?.price
                auxCurrency = product?.currency

                const subCategoryID = product?.c_subCategoryID || ''
                const categories = getItemCategories(categoriesTree, subCategoryID)

                return {
                    item_name: product?.product_name || product?.productName || '',
                    item_id: product?.c_orliweb_product_short_code,
                    item_ean: product?.ean || '',
                    item_color: product?.variationValues.color || '',
                    item_size: product?.variationValues?.size || '',
                    item_print: product?.c_orliweb_stat20_mat_rgt || '',
                    ...categories,
                    price: finalPrice,
                    quantity: 1, // Always 1 for view item list
                    discount: finalDiscount,
                    currency: product?.currency
                }
            } else if (format === 'homepage') {
                const subCategoryID = product?.representedProduct?.c_subCategoryID || ''
                const categories = getItemCategories(categoriesTree, subCategoryID)
                // Handle the alternative data format
                const productLowestPrice = findLowestPrice(product)
                const discount =
                    productLowestPrice.promotion == null
                        ? 0
                        : Number(product?.price - productLowestPrice.minPrice)
                const finalDiscount = conversionsRate?.lcpMultiplier
                    ? conversionsRate?.lcpMultiplier * discount
                    : discount
                const finalPrice = conversionsRate?.lcpMultiplier
                    ? conversionsRate?.lcpMultiplier * product?.price
                    : product?.price
                auxCurrency = product?.currency
                return {
                    item_name: product?.name,
                    item_id: product?.c_orliweb_product_short_code,
                    item_ean: product?.ean || '',
                    item_color: product?.variationValues.color || '',
                    item_size: product?.variationValues?.size || '',
                    item_print: product?.c_orliweb_stat20_mat_rgt || '',
                    ...categories,
                    price: finalPrice,
                    quantity: 1, // Always 1 for view item list
                    discount: finalDiscount,
                    currency: product?.currency
                }
            } else {
                const subCategoryID = product?.representedProduct?.c_subCategoryID || ''
                const categories = getItemCategories(categoriesTree, subCategoryID)

                const variant = product?.variants[0]
                const promotionalPrice =
                    variant.productPromotions?.[0]?.promotionalPrice || variant.price
                const discount = variant.price - promotionalPrice
                auxCurrency = product?.currency
                const finalPrice = conversionsRate?.lcpMultiplier
                    ? conversionsRate?.lcpMultiplier * product?.price
                    : product.price
                const finalDiscount = conversionsRate?.lcpMultiplier
                    ? conversionsRate?.lcpMultiplier * discount
                    : discount
                return {
                    item_name: product?.productName,
                    item_id: product?.representedProduct.c_orliweb_product_short_code,
                    item_ean: product?.representedProduct.id || '',
                    item_color: product?.representedProduct.c_color || '',
                    item_size: product?.representedProduct?.variationValues?.size || '',
                    item_print: product?.representedProduct?.c_orliweb_stat20_mat_rgt || '',
                    ...categories,
                    price: finalPrice,
                    quantity: 1, // Always 1 for view item list
                    discount: finalDiscount,
                    currency: product?.currency
                }
            }
        })

        return {
            currency: conversionsRate?.paymentCurrencyCode || basket?.currency || auxCurrency,
            items: items
        }
    } catch (e) {
        return {
            currency: null,
            items: null
        }
    }
}

/**
 * Constructs an object containing analytics data for menu click events based on the category hierarchy.
 *
 * @param {Object} category - The category object containing information about the category hierarchy.
 * @param {string} category.parentCategoryId - The ID of the parent category. If 'root', the category is a top-level category.
 * @param {string} category.name - The name of the current category.
 * @param {Array<Object>} category.parentCategoryTree - An array representing the hierarchy of parent categories.
 * @param {string} category.parentCategoryTree[].name - The name of a parent category at a specific level in the hierarchy.
 *
 * @returns {Object} An object containing the menu levels for analytics purposes.
 * @returns {string} return.menu_level1 - The name of the level 1 menu, typically the top-level category.
 * @returns {string} return.menu_level2 - The name of the level 2 menu, typically a sub-category.
 * @returns {string} return.menu_level3 - The name of the level 3 menu, typically a further sub-category.
 */
/**
 * Constructs an object containing analytics data for menu click events based on the category hierarchy.
 *
 * @param {Object} category - The category object containing information about the category hierarchy.
 * @param {string} category.parentCategoryId - The ID of the parent category. If 'root', the category is a top-level category.
 * @param {string} category.name - The name of the current category.
 * @param {Array<Object>} category.parentCategoryTree - An array representing the hierarchy of parent categories.
 * @param {string} category.parentCategoryTree[].name - The name of a parent category at a specific level in the hierarchy.
 *
 * @returns {Object} An object containing the menu levels for analytics purposes.
 * @returns {string} return.menu_level1 - The name of the level 1 menu, typically the top-level category.
 * @returns {string} return.menu_level2 - The name of the level 2 menu, typically a sub-category.
 * @returns {string} return.menu_level3 - The name of the level 3 menu, typically a further sub-category.
 */
export const buildAnalyticsClickMenuData = (category) => {
    try {
        let menuLevel1 = ''
        let menuLevel2 = ''
        let menuLevel3 = ''

        if (category?.parentCategoryId === 'root') {
            // If the parent category is 'root', extract the level 1 menu
            menuLevel1 = category?.name
        } else {
            // Otherwise, extract the level 2 and 3 menus
            menuLevel1 = category?.parentCategoryTree[0]?.name || ''
            menuLevel2 = category?.parentCategoryTree[1]?.name || ''
            menuLevel3 = category?.parentCategoryTree[2]?.name || ''
        }

        return {
            menu_level1: menuLevel1,
            menu_level2: menuLevel2,
            menu_level3: menuLevel3
        }
    } catch (error) {
        // Return a default object in case of error
        return {
            menu_level1: '',
            menu_level2: '',
            menu_level3: ''
        }
    }
}

/**
 * Builds an analytics data object for a product view event, formatting product and category details
 * for use in analytics platforms (e.g., Google Analytics 4). Handles both standard and alternative
 * product structures, computes discounts, applies currency conversion, and extracts relevant product
 * attributes such as color, size, print, and family.
 *
 * @param {Object} product - The product object to extract analytics data from. May contain a 'representedProduct' for alternative structures.
 * @param {Object} categoriesTree - The hierarchical category tree used to resolve product categories.
 * @param {Object} [conversionRate] - Optional conversion rate object, may include 'lcpMultiplier' and 'paymentCurrencyCode' for currency conversion.
 * @returns {Object} An analytics data object with the following structure:
 *   {
 *     currency: {string|null}, // The currency code used for the analytics event.
 *     items: [
 *       {
 *         item_name: {string},      // Name of the product.
 *         item_id: {string},        // Product short code or identifier.
 *         item_ean: {string},       // EAN code of the product.
 *         item_color: {string},     // Color attribute of the product.
 *         item_family: {string},    // Family attribute of the product.
 *         ...categories,            // Category fields resolved from the category tree.
 *         price: {number},          // Final price, possibly converted.
 *         quantity: {number},       // Always 1 for view item events.
 *         discount: {number},       // Calculated discount, possibly converted.
 *         currency: {string},       // Original currency code.
 *         item_size: {string},      // Size attribute of the product.
 *         item_print: {string}      // Print attribute of the product.
 *       }
 *     ]
 *   }
 *   If an error occurs, returns an object with currency: null and items: [].
 */
export const buildAnalyticsViewItemData = (product, categoriesTree, conversionRate) => {
    try {
        const isAlternativeStructure = product.representedProduct !== undefined

        const {
            name: item_name = product.productName,
            ean = isAlternativeStructure ? product.representedProduct.id : '',
            variationValues = {},
            c_orliweb_isFamily: item_family = isAlternativeStructure
                ? product.representedProduct.c_orliweb_isFamily
                : '',
            price = product.price,
            currency = product.currency
        } = product

        let color = ''
        let subCategoryID = ''
        let item_id = ''
        let product_ean = ean
        let size = ''
        let print = ''

        if (isAlternativeStructure) {
            color = product?.representedProduct?.c_color
            size = product?.representedProduct?.c_size
            print = product?.representedProduct?.c_orliweb_stat20_mat_rgt
            subCategoryID = product?.representedProduct?.c_subCategoryID
            item_id = product?.representedProduct?.c_orliweb_product_short_code
        } else {
            color = variationValues.color
            size = variationValues.size
            print = product?.c_orliweb_stat20_mat_rgt
            subCategoryID = product?.c_subCategoryID
            item_id = product?.c_orliweb_product_short_code
            if (!product_ean) {
                product_ean = getEANForProduct(product)
            }
        }
        const categories = getItemCategories(categoriesTree, subCategoryID)
        const productLowestPrice = findLowestPrice(product)
        const discount =
            productLowestPrice.promotion == null ? 0 : Number(price - productLowestPrice.minPrice)
        const finalDiscount = conversionRate?.lcpMultiplier
            ? conversionRate?.lcpMultiplier * discount
            : discount
        const finalPrice = conversionRate?.lcpMultiplier
            ? conversionRate?.lcpMultiplier * price
            : price

        return {
            currency: conversionRate?.paymentCurrencyCode || currency,
            items: [
                {
                    item_name,
                    item_id: item_id || '',
                    item_ean: product_ean || '',
                    item_color: color || '',
                    item_family,
                    ...categories,
                    price: finalPrice,
                    quantity: 1, // Always 1 for view item
                    discount: finalDiscount,
                    currency,
                    item_size: size || '',
                    item_print: print || ''
                }
            ]
        }
    } catch (error) {
        // Return a default or empty object in case of error
        return {
            currency: null,
            items: []
        }
    }
}

// const getItemCategories = (categoriesTree, subCategoryID) => {
//     let itemCategory = ''
//     let itemCategory2 = ''
//     let itemCategory3 = ''
//     let itemCategory4 = ''

//     const findCategoryPath = (categories, targetId, path = []) => {
//         for (const category of categories) {
//             const newPath = [...path, category]
//             if (category.id === targetId) {
//                 return newPath
//             }
//             if (category.categories) {
//                 const result = findCategoryPath(category.categories, targetId, newPath)
//                 if (result) {
//                     return result
//                 }
//             }
//         }
//         return null
//     }

//     const categoryPath = findCategoryPath(categoriesTree.categories, subCategoryID)

//     if (categoryPath) {
//         itemCategory = categoryPath[0]?.id || ''
//         itemCategory2 = categoryPath[1]?.id || ''
//         itemCategory3 = categoryPath[2]?.id || ''
//         itemCategory4 = categoryPath[3]?.id || ''
//     }

//     return {
//         item_category: itemCategory,
//         item_category2: itemCategory2,
//         item_category3: itemCategory3,
//         item_category4: itemCategory4
//     }
// }

const getItemCategories = (categoryLookup, subCategoryID) => {
    const categoryLevels = ['item_category', 'item_category2', 'item_category3', 'item_category4']
    const categoryResult = {}

    const categoryPath = categoryLookup[subCategoryID] || []

    categoryPath.forEach((category, index) => {
        if (index < categoryLevels.length) {
            categoryResult[categoryLevels[index]] = category || ''
        }
    })

    // Ensure all category levels are present in the result
    categoryLevels.forEach((level) => {
        if (!categoryResult[level]) {
            categoryResult[level] = ''
        }
    })

    return categoryResult
}

/**
 * Extracts up to four category IDs from a product's structured data categories and maps them to item_category fields.
 * @param {Object} product - The product object containing structured data categories.
 * @param {Array} [product.c_structuredDataCategories] - An optional array of category objects, each with an 'id' property.
 * @returns {Object} An object with properties item_category, item_category2, item_category3, and item_category4, each containing the corresponding category ID or an empty string if not available.
 */
const getCategoriesFromStructuredData = (product) => {
    const categories = Array.isArray(product?.c_structuredDataCategories)
        ? product.c_structuredDataCategories
        : []

    return {
        item_category: categories[0]?.id || '',
        item_category2: categories[1]?.id || '',
        item_category3: categories[2]?.id || '',
        item_category4: categories[3]?.id || ''
    }
}

/**
 * Builds an analytics data object representing the currently applied filters and sorting option.
 *
 * Extracts selected filter values (color, collection, pattern, size) and the selected sorting option
 * from the provided refinements and sorting data. Selected filter values are concatenated into pipe-delimited
 * strings for analytics tracking.
 *
 * @param {Object} refinementsData - Object containing available refinements and selected refinement values.
 * @param {Array<Object>} refinementsData.refinements - List of available refinement objects, each with attributeId and values.
 * @param {Object} refinementsData.selectedRefinements - Mapping of attributeId to array of selected values.
 * @param {Object} sortingData - Object containing sorting options and the selected sorting option.
 * @param {Array<Object>} sortingData.sortingOptions - List of available sorting option objects, each with id and label.
 * @param {string} sortingData.selectedSortingOption - The id of the currently selected sorting option.
 * @returns {Object} Analytics filter data object with the following properties:
 *   - {string} filter_sort_by - The label of the selected sorting option.
 *   - {string} filter_color - Pipe-delimited string of selected color labels.
 *   - {string} filter_collection - Pipe-delimited string of selected collection labels.
 *   - {string} filter_pattern - Pipe-delimited string of selected pattern labels.
 *   - {string} filter_size - Pipe-delimited string of selected size labels.
 */
export const buildAnalyticsApplyFilterData = (refinementsData, sortingData) => {
    const {refinements, selectedRefinements} = refinementsData
    const {sortingOptions, selectedSortingOption} = sortingData

    const getSelectedLabels = (attributeId) => {
        const refinement = refinements.find((ref) => ref.attributeId === attributeId)
        if (!refinement) return []
        return refinement.values
            .filter((value) => selectedRefinements[attributeId]?.includes(value.value))
            .map((value) => value.label)
    }

    // Extract selected filter values
    const selectedColors = getSelectedLabels('c_orliweb_color_code_rgt')
    const selectedCollections = getSelectedLabels('c_orliweb_collection')
    const selectedPatterns = getSelectedLabels('c_orliweb_motif')
    const selectedSizes = getSelectedLabels('c_size')

    // Convert arrays to strings
    const filterColor = selectedColors.join('|')
    const filterCollection = selectedCollections.join('|')
    const filterPattern = selectedPatterns.join('|')
    const filterSize = selectedSizes.join('|')

    // Determine the selected sorting option
    const filterSortBy =
        sortingOptions?.find((option) => option.id === selectedSortingOption)?.label || ''

    return {
        filter_sort_by: filterSortBy,
        filter_color: filterColor,
        filter_collection: filterCollection,
        filter_pattern: filterPattern,
        filter_size: filterSize
    }
}

/**
 * Builds an analytics data object representing the applied store filters and search term.
 *
 * Iterates over the provided store filter categories, extracts the selected options based on the filters object,
 * and maps them to their corresponding analytics keys. The resulting object contains the search term and
 * pipe-separated strings of selected filter labels for collections, store types, and services.
 *
 * @param {Array<Object>} storeFilterCategories - Array of filter category objects, each with an id and options array.
 * @param {string} searchTerm - The search term entered by the user.
 * @param {Object} filters - An object mapping option names to boolean values indicating selection.
 * @returns {Object} An object containing the search term and selected filter values for analytics, with keys:
 *   - search_term {string}
 *   - filter_store_loc_collections {string}
 *   - filter_store_loc_store_type {string}
 *   - filter_store_loc_services {string}
 */
export const buildAnalyticsApplyStoreFilterData = (storeFilterCategories, searchTerm, filters) => {
    const filterData = {}

    storeFilterCategories.forEach((category) => {
        const selectedOptions = category.options
            .filter((option) => filters[option.name])
            .map((option) => option.label)
            .join('|')

        if (category.id === 'collections') {
            filterData.filter_store_loc_collections = selectedOptions
        } else if (category.id === 'types') {
            filterData.filter_store_loc_store_type = selectedOptions
        } else if (category.id === 'services') {
            filterData.filter_store_loc_services = selectedOptions
        }
    })

    return {
        search_term: searchTerm,
        filter_store_loc_collections: filterData.filter_store_loc_collections || '',
        filter_store_loc_store_type: filterData.filter_store_loc_store_type || '',
        filter_store_loc_services: filterData.filter_store_loc_services || ''
    }
}

/**
 * Builds and returns analytics label data for a family of products.
 * @param {Object} family - The family object containing product information.
 * @param {string} family.id - The unique identifier for the family.
 * @param {string} family.name - The display name of the family.
 * @param {Array<Object>} family.products - An array of product objects within the family.
 * @returns {Object} An object containing structured analytics label data for the specified family.
 */
export const buildAnalyticsFamilyLabelData = (tag, linkUrl) => {
    return {
        link_url: linkUrl,
        link_text: tag
    }
}

/**
 * Retrieves the EAN (European Article Number) for a given product.
 * @param {dw.catalog.Product} product - The product object for which to obtain the EAN.
 * @returns {string|null} The EAN code as a string if available, otherwise null.
 */
const getEANForProduct = (product) => {
    if (!product || !product.variants || !Array.isArray(product.variants)) {
        return null
    }

    const variationAttributes = product.variationAttributes || []

    // Helper function to sort variants based on attribute order
    const sortVariantsByAttributes = (variants, attributes) => {
        return attributes.reduce((sortedVariants, attribute) => {
            const {id, values} = attribute
            const valueOrder = values.map((value) => value.value)

            return sortedVariants.sort((a, b) => {
                const aIndex = valueOrder.indexOf(a.variationValues[id])
                const bIndex = valueOrder.indexOf(b.variationValues[id])
                return aIndex - bIndex
            })
        }, variants)
    }

    const isMasterProduct = product.type.master
    const isVariationMaster = product.type.variationGroup

    if (isMasterProduct) {
        // For master products, sort and find the first color and size variation
        const sortedVariants = sortVariantsByAttributes(product.variants, variationAttributes)
        return sortedVariants.length > 0 ? sortedVariants[0].productId : null
    } else if (isVariationMaster) {
        // For variation masters, sort and find the first size variation
        const sizeAttribute = variationAttributes.find((attr) => attr.id === 'size')
        if (sizeAttribute) {
            const sortedSizeVariants = sortVariantsByAttributes(product.variants, [sizeAttribute])
            return sortedSizeVariants.length > 0 ? sortedSizeVariants[0].productId : null
        }
    } else if (product?.ean) {
        // for variation products
        // return the ean
        return product.ean
    }

    return null
}
